import { Box, Step, StepLabel, Stepper, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { StepConfig, useStepper } from "../../context/stepper-provider";

interface HorizontalStepperProps {
  showLabels?: boolean;
  allowStepClick?: boolean;
  alternativeLabel?: boolean;
}

const StepIcon = (props: { active?: boolean; completed?: boolean }) => {
  const { completed, active } = props;

  if (completed) {
    return (
      <CheckCircleIcon
        sx={{
          color: "success.main",
          fontSize: 28,
        }}
      />
    );
  }

  if (active) {
    return (
      <RadioButtonUncheckedIcon
        sx={{
          color: "primary.main",
          fontSize: 28,
          backgroundColor: "white",
          borderRadius: "50%",
        }}
      />
    );
  }

  return (
    <RadioButtonUncheckedIcon
      sx={{
        color: "grey.400",
        fontSize: 28,
      }}
    />
  );
};

const HorizontalStepper = ({
  showLabels = true,
  allowStepClick = true,
  alternativeLabel = false,
}: HorizontalStepperProps) => {
  const { activeStep, steps, goToStep, isStepCompleted, canGoToStep } =
    useStepper();

  const handleStepClick = (stepIndex: number) => {
    if (allowStepClick && canGoToStep(stepIndex)) {
      goToStep(stepIndex);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        py: 2,
        px: 3,
        backgroundColor: "background.paper",
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stepper activeStep={activeStep} alternativeLabel={alternativeLabel}>
        {steps.map((step: StepConfig, index: number) => {
          const completed = isStepCompleted(index);
          const active = activeStep === index;
          const clickable = allowStepClick && canGoToStep(index);

          const getLabelColor = () => {
            if (completed) return "success.main";
            if (active) return "text.primary";
            return "text.secondary";
          };

          return (
            <Step key={step.id} completed={completed}>
              <StepLabel
                StepIconComponent={StepIcon}
                StepIconProps={{ active, completed }}
                optional={
                  step.optional && showLabels ? (
                    <Typography variant="caption">Optional</Typography>
                  ) : null
                }
                sx={{
                  cursor: clickable ? "pointer" : "default",
                  "& .MuiStepLabel-label": {
                    color: getLabelColor(),
                    fontWeight: active ? 600 : 400,
                  },
                }}
                onClick={() => handleStepClick(index)}
              >
                {showLabels && step.label}
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};

export default HorizontalStepper;
